import { Button, Input, Tab, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Tabs } from '@mui/material';
import { Link, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { debounce, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import withStyles from '@mui/styles/withStyles';

import { CLEAR_USER_ACCESS_CODE_SUCCESS, TOGGLE_USER_FLAG_SUCCESS, clearUserAccessCodes, searchUsers, toggleUserFlag } from './users.actions';
import { handleToastMessage, setPageTitle } from '../layout/layout.actions';
import Colors from '../../styles/colors';
import DeleteUserButton from './deleteUserButton.component';

const SEARCH_FIELDS_DEBOUNCE_TIME_MS = 400;
const STORAGE_KEY_ROWS_PER_PAGE = 'rowsPerPage';
const STORAGE_KEY_LIST_STATE = 'usersListState';

const DATA_COLUMNS = [
  { fieldName: 'firstName', displayName: 'First Name' },
  { fieldName: 'lastName', displayName: 'Last Name' },
  { fieldName: 'email', displayName: 'Email Address' },
  { fieldName: 'firebaseId', displayName: 'Firebase ID' },
];

class UsersContainer extends Component {
  constructor(props) {
    super(props);

    ['handleToggleFlag'].map((key) => (this[key] = this[key].bind(this)));
    this.debouncedFetchUsers = debounce(() => this.fetchUsers(), SEARCH_FIELDS_DEBOUNCE_TIME_MS);
  }

  state = {
    page: 0,
    rowsPerPage: 10,
    filters: {
      firstName: '',
      lastName: '',
      email: '',
      firebaseId: '',
    },
    tabIndex: 0,
  };

  componentDidMount() {
    this.props.setPageTitle('Users');
    this.restorePersistedState();
  }

  componentWillUnmount() {
    this.debouncedFetchUsers.cancel();
    const { page, filters } = this.state;
    sessionStorage.setItem(STORAGE_KEY_LIST_STATE, JSON.stringify({ page, filters }));
  }

  restorePersistedState() {
    const savedRowsPerPage = parseInt(localStorage.getItem(STORAGE_KEY_ROWS_PER_PAGE), 10);
    const savedListState = JSON.parse(sessionStorage.getItem(STORAGE_KEY_LIST_STATE));

    const restoredState = {
      ...(savedRowsPerPage >= 0 && { rowsPerPage: savedRowsPerPage }),
      ...(savedListState && { page: savedListState.page, filters: savedListState.filters }),
    };

    const alreadyLoaded = this.props.users.length > 0;
    const hasRestoredState = Object.keys(restoredState).length > 0;

    if (hasRestoredState) {
      this.setState(restoredState, () => { if (!alreadyLoaded) this.fetchUsers(); });
    } else if (!alreadyLoaded) {
      this.fetchUsers();
    }
  }

  fetchUsers() {
    const { page, rowsPerPage, filters } = this.state;
    this.props.searchUsers({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      ...filters,
    });
  }

  handleFilterChange = (field, value) => {
    this.setState(
      (prevState) => ({
        filters: { ...prevState.filters, [field]: value },
        page: 0,
      }),
      () => this.debouncedFetchUsers()
    );
  };

  handleChangePage = (event, page) => {
    this.setState({ page }, () => this.fetchUsers());
  };

  handleChangeRowsPerPage = (event) => {
    localStorage.setItem(STORAGE_KEY_ROWS_PER_PAGE, event.target.value);
    this.setState({ rowsPerPage: event.target.value, page: 0 }, () => this.fetchUsers());
  };

  async handleToggleFlag(userId, flag) {
    let userIndex = findIndex(this.props.users, { id: userId });
    let { users } = this.props;
    let user = users[userIndex];
    let hasAccess = user[flag];

    let question = `Are you sure you wish to ${hasAccess ? 'revoke' : 'grant'} 'debug'} access ${
      hasAccess ? 'from' : 'to'
    } ${user.firstName} ${user.lastName}?`;
    let statement = ' Doing this will require that user to logout and then login again.';

    if (!this.props.isToggling && window.confirm(question + statement)) {
      let response = await this.props.toggleUserFlag(userId, flag);
      if (response.type === TOGGLE_USER_FLAG_SUCCESS) {
        this.props.handleToastMessage('User updated.');
      } else {
        this.props.handleToastMessage('User update failed.');
      }
    }
  }

  async handleClearAccessCodes(userId) {
    const response = await this.props.clearUserAccessCodes(userId);
    if (response.type === CLEAR_USER_ACCESS_CODE_SUCCESS) {
      this.props.handleToastMessage('Access codes successfuly cleared.');
    } else {
      this.props.handleToastMessage('Access codes clearing failed.');
    }
  }

  render() {
    let { classes } = this.props;
    let { tabIndex } = this.state;

    return (
      <Fragment>
        <Tabs className={classes.tabs} value={this.state.tabIndex} onChange={(e, value) => this.setState({ tabIndex: value })}>
          <Tab label="Users List" className={classes.tab} />
        </Tabs>
        {tabIndex === 0 && (
          <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: '75vh', paddingTop: 10, overflowY: 'auto' }}>
            {this.props.isLoading && <div className="loader" />}
            {!this.props.isLoading && (
              <div className={classes.tableWrapper} id="tableWrapper">
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      {DATA_COLUMNS.map((column, i) => {
                        return (
                          <TableCell className={classes.head} key={i}>
                            <div>{column.displayName}</div>
                            <div>
                              <Input
                                margin="none"
                                id={`filter_${column.fieldName}`}
                                variant="caption"
                                placeholder="filter"
                                className={classes.textField}
                                classes={{
                                  root: classes.root,
                                }}
                                fullWidth
                                value={this.state.filters[column.fieldName]}
                                onChange={(e) => this.handleFilterChange(column.fieldName, e.target.value)}
                                type={column.type ? column.type : 'text'}
                              />
                            </div>
                          </TableCell>
                        );
                      })}

                      <TableCell className={classes.head}>
                        <div>Actions</div>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.props.users.map((user, i) => {
                      return (
                        <TableRow key={user.id}>
                          {DATA_COLUMNS.map((column, i) => {
                            return (
                              <TableCell className={classes.cell} component="td" scope="row" key={i}>
                                {user[column.fieldName]}
                              </TableCell>
                            );
                          })}

                          <TableCell className={classes.cell}>
                            <Button
                              variant="contained"
                              color={'secondary'}
                              className={classes.actionButton}
                              component={Link}
                              to={`/users/${user.id}`}
                            >
                              View
                            </Button>
                            <Button
                              variant="contained"
                              color={user.canDebug ? 'primary' : 'secondary'}
                              className={classes.actionButton}
                              onClick={() => this.handleToggleFlag(user.id, 'canDebug')}
                            >
                              Debug BLE
                            </Button>
                            {!!user.accessCodes.length && (
                              <Button
                                variant="contained"
                                color={'primary'}
                                className={classes.actionButton}
                                onClick={() => this.handleClearAccessCodes(user.id)}
                              >
                                Clear Access Codes
                              </Button>
                            )}
                            <DeleteUserButton
                              user={user}
                              className={classes.actionButton}
                              onDeleted={() => this.fetchUsers()}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {!this.props.isLoading && (
              <div style={{ flex: 1, paddingRight: 100 }}>
                <TablePagination
                  component="div"
                  count={this.props.totalCount}
                  rowsPerPage={this.state.rowsPerPage}
                  page={this.state.page}
                  style={{ flex: 0 }}
                  backIconButtonProps={{
                    'aria-label': 'Previous Page',
                  }}
                  nextIconButtonProps={{
                    'aria-label': 'Next Page',
                  }}
                  onPageChange={this.handleChangePage}
                  onRowsPerPageChange={this.handleChangeRowsPerPage}
                />
              </div>
            )}

          </div>
        )}
      </Fragment>
    );
  }
}

const styles = (theme) => ({
  root: {
    flex: 1,
    marginTop: theme.spacing(3),
    margin: 20,
  },
  actionButton: {
    marginRight: '1rem',
  },
  tableWrapper: {
    padding: '1rem',
    overflowX: 'auto',
  },
  tableBody: {
    overflowY: 'auto',
    height: '75vh',
  },
  button: {
    margin: theme.spacing(1),
    position: 'absolute',
    bottom: 10,
    right: 10,
  },
  head: {
    position: 'sticky',
    top: 0,
    backgroundColor: Colors.white,
  },
  textField: {
    boxShadow: 'none',
    fontWeight: 'normal',
    border: 0,
    marginTop: 5,
    marginBottom: 5,
    marginLeft: 0,
    marginRight: 0,
    fontSize: 12,
  },
  cell: {
    color: Colors.primary.main,
  },
  input: {
    height: 20,
  },
  link: {
    color: Colors.primary.main,
    textDecoration: 'none',
  },
  tabs: {
    backgroundColor: Colors.secondary.main,
  },
  tab: {
    color: Colors.white,
    fontWeight: 'bold',
  },
});

UsersContainer.propTypes = {
  users: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isToggling: PropTypes.bool.isRequired,
  errors: PropTypes.array.isRequired,

  setPageTitle: PropTypes.func.isRequired,
  searchUsers: PropTypes.func.isRequired,
  toggleUserFlag: PropTypes.func.isRequired,
  clearUserAccessCodes: PropTypes.func.isRequired,
  handleToastMessage: PropTypes.func.isRequired,

  classes: PropTypes.object.isRequired,

  // Injected by React Router
  history: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  return {
    users: state.users.get('users'),
    totalCount: state.users.get('totalCount'),
    isLoading: state.users.get('isLoading'),
    isToggling: state.users.get('isToggling'),
    errors: state.locations.get('errors'),
  };
};

const prepareForExport = compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
  connect(
    mapStateToProps,
    {
      setPageTitle,
      searchUsers,
      handleToastMessage,
      toggleUserFlag,
      clearUserAccessCodes,
    }
  ),
);

export default prepareForExport(UsersContainer);
